import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { LabsStore } from "@/Labs/Stores/LabsStore";

class LabActionsInboxSettingsContainerHandler extends BaseOperationHandler {
    public constructor() {
        super();

        this.RegisterOperation(LocalOperations.Labs.LabActionsInboxSearchSettings_ClinicianActionByFilterTextChanged, this.ClinicianActionByFilterTextChanged);
        this.RegisterOperation(LocalOperations.Labs.LabActionsInboxSearchSettings_ClinicianPrescriptorFilterTextChanged, this.ClinicianPrescriptorFilterTextChanged);
        this.RegisterOperation(LocalOperations.Labs.LabActionsInboxSearchSettings_OnOkClick, this.OnOkClick);
        this.RegisterOperation(LocalOperations.Labs.LabActionsInboxSearchSettings_OnCancelClick, this.OnCancelClick);
        this.RegisterOperation(LocalOperations.Labs.LabActionsInboxSearchSettings_OnResultsPerPageChange, this.OnResultsPerPageChange);
        this.RegisterOperation(LocalOperations.Labs.LabActionsInboxSearchSettings_ShowOptionsButtonClick, this.ToggleSearchOptionsDropdown);
        this.RegisterOperation(LocalOperations.Labs.LabActionsInboxColumnOrderingPopup_Open, this.ShowColumnOrderingPopup);
        this.RegisterOperation(LocalOperations.Labs.LabActionsInboxColumnOrderingPopup_Close, this.CloseColumnOrderingPopup);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.OnContainerClick, false);
        this.RegisterOperation(LocalOperations.Labs.LabActionsInboxSearchSettings_CheckboxChange, this.OnSettingsContainerCheckboxChange);
        this.RegisterOperation(LocalOperations.Labs.LabActionsInboxSearchSettings_FromDateChanged, this.FromDateChanged);
        this.RegisterOperation(LocalOperations.Labs.LabActionsInboxSearchSettings_ToDateChanged, this.ToDateChanged);
    }

    private FromDateChanged(request: LocalEvent, store: LabsStore): void {
        store.LabsActionInboxModel = store.LabsActionInboxModel.set("DateFrom", request.Get<Date>("value"));
    }

    private ToDateChanged(request: LocalEvent, store: LabsStore): void {
        store.LabsActionInboxModel = store.LabsActionInboxModel.set("DateTo", request.Get<Date>("value"));
    }

    private OnSettingsContainerCheckboxChange(request: LocalEvent, store: LabsStore): void {
        var target = request.Get<CheckboxTarget>("target");
        var checked = request.Get<boolean>("checked");
        var id = request.Get<string>("id");

        switch (target) {
            case CheckboxTarget.Myle_Labs_Sites:
                store.LabsActionInboxModel = store.LabsActionInboxModel.set("Sites", store.helper.FindUpdateICheckableItem<SiteUIModel>(store.LabsActionInboxModel.get("Sites"), id, checked));
                // apply site selection to both:
                // PrescriptorClinicians
                // ActionByClinicians.

                var prescriptorClinicians = store.UpdateVisibilityToCliniciansFromSelectedSites(store.LabsActionInboxModel.get("PrescriptorClinicians"));
                var actionByClinicians = store.UpdateVisibilityToCliniciansFromSelectedSites(store.LabsActionInboxModel.get("ActionByClinicians"));
                store.LabsActionInboxModel = store.LabsActionInboxModel.set("PrescriptorClinicians", prescriptorClinicians);
                store.LabsActionInboxModel = store.LabsActionInboxModel.set("ActionByClinicians", actionByClinicians);

                break;
            case CheckboxTarget.Myle_Labs_Clinicians:
                if (!checked && store.LabsActionInboxModel.get("SelectAllCliniciansPrescriptorChecked")) {
                    store.LabsActionInboxModel = store.LabsActionInboxModel.set("SelectAllCliniciansPrescriptorChecked", false);
                }
                let currentA = store.LabsActionInboxModel.get("PrescriptorClinicians").map((c: EmployeeUIModel) => { if (c.Id === id) { c.Checked = checked; } return c; }).toList();
                store.LabsActionInboxModel = store.LabsActionInboxModel.set("PrescriptorClinicians", currentA);
                break;
            case CheckboxTarget.Myle_Labs_Clinicians_List2:
                if (!checked && store.LabsActionInboxModel.get("SelectAllCliniciansActionByChecked")) { store.LabsActionInboxModel = store.LabsActionInboxModel.set("SelectAllCliniciansActionByChecked", false); }
                let currentB = store.LabsActionInboxModel.get("ActionByClinicians").map((c: EmployeeUIModel) => { if (c.Id === id) { c.Checked = checked; } return c; }).toList();
                store.LabsActionInboxModel = store.LabsActionInboxModel.set("ActionByClinicians", currentB);
                break;
            case CheckboxTarget.Myle_Labs_SitesControlCheck:
                store.LabsActionInboxModel = store.LabsActionInboxModel.set("SelectAllSitesChecked", checked);
                var sites = store.helper.ChangeCheckStateFromAllICheckable(store.LabsActionInboxModel.get("Sites"), checked);
                store.LabsActionInboxModel = store.LabsActionInboxModel.set("Sites", sites);

                var prescriptorClinicians2 = store.UpdateVisibilityToCliniciansFromSelectedSites(store.LabsActionInboxModel.get("PrescriptorClinicians"));
                var actionByClinicians2 = store.UpdateVisibilityToCliniciansFromSelectedSites(store.LabsActionInboxModel.get("ActionByClinicians"));
                store.LabsActionInboxModel = store.LabsActionInboxModel.set("PrescriptorClinicians", prescriptorClinicians2);
                store.LabsActionInboxModel = store.LabsActionInboxModel.set("ActionByClinicians", actionByClinicians2);
                break;
            case CheckboxTarget.Myle_Labs_AllCliniciansPrescriptorControlCheck:
                store.LabsActionInboxModel = store.LabsActionInboxModel.set("SelectAllCliniciansPrescriptorChecked", checked);

                // apply filters to "visible" items. Note: visible really implies "included" and not to be confused with filtering by text (name) of clinician.
                // that text search does not affect visibility state but simply filters out items at the UI level

                var prescriptorClinicians3 = store.LabsActionInboxModel.get("PrescriptorClinicians").map(c => { if (c.Visible) { c.Checked = checked; } return c; }).toList();
                store.LabsActionInboxModel = store.LabsActionInboxModel.set("PrescriptorClinicians", prescriptorClinicians3);
                break;

            case CheckboxTarget.Myle_Labs_AllCliniciansActionByControlCheck:
                store.LabsActionInboxModel = store.LabsActionInboxModel.set("SelectAllCliniciansActionByChecked", checked);

                // apply filters to "visible" items. Note: visible really implies "included" and not to be confused with filtering by text (name) of clinician.
                // that text search does not affect visibility state but simply filters out items at the UI level

                var actionByClinicians3 = store.LabsActionInboxModel.get("ActionByClinicians").map(c => { if (c.Visible) { c.Checked = checked; } return c; }).toList();
                store.LabsActionInboxModel = store.LabsActionInboxModel.set("ActionByClinicians", actionByClinicians3);
                break;

            default:
                break;
        }
    }

    private ShowColumnOrderingPopup(request: LocalEvent, store: LabsStore): void {
        store.LabsActionInboxModel = store.LabsActionInboxModel.set("ColumnOrderingPopupOpen", true);
        store.LabsActionInboxModel = store.LabsActionInboxModel.set("LabActionsInboxDisplaySettingsOption", false);
        sessionStore.showGlobalOverlay(true);
    }

    private CloseColumnOrderingPopup(request: LocalEvent, store: LabsStore): void {
        store.LabsActionInboxModel = store.LabsActionInboxModel.set("ColumnOrderingPopupOpen", false);
        store.LabsActionInboxModel = store.LabsActionInboxModel.set("LabActionsInboxDisplaySettingsOption", false);
        sessionStore.hideGlobalOverlay();
    }

    private ClinicianActionByFilterTextChanged(request: LocalEvent, store: LabsStore): void {
        store.LabsActionInboxModel = store.LabsActionInboxModel.set("CliniciansActionBySearchText", request.Get<string>("value"));
    }

    private ClinicianPrescriptorFilterTextChanged(request: LocalEvent, store: LabsStore): void {
        store.LabsActionInboxModel = store.LabsActionInboxModel.set("CliniciansPrescriptorSearchText", request.Get<string>("value"));
    }

    private OnOkClick(request: LocalEvent, store: LabsStore): void {
        store.LabsActionInboxModel = store.LabsActionInboxModel.set("LabActionsInboxDisplaySettingsOption", false);
        labsStore.LabActions.RefreshActions(store.LabsActionInboxModel.get("CurrentInbox"), store.LabsActionInboxModel.get("GlobalActionSearchText"), store.LabsActionInboxModel.get("SortColumn"), store.LabsActionInboxModel.get("SortColumnDirection"));
    }
    private OnCancelClick(request: LocalEvent, store: LabsStore): void {
        store.LabsActionInboxModel = store.LabsActionInboxModel.set("LabActionsInboxDisplaySettingsOption", false);
    }

    private OnResultsPerPageChange(request: LocalEvent, store: LabsStore): void {
        store.LabsActionInboxModel = store.LabsActionInboxModel.set("SelectedPageSizeOption", request.Get<number>("value"));
        store.LabsActionInboxModel = store.LabsActionInboxModel.set("SelectedPageNumber", 1);
    }

    private ToggleSearchOptionsDropdown(request: LocalEvent, store: LabsStore): void {
        var open = !store.LabsActionInboxModel.get("LabActionsInboxDisplaySettingsOption");
        store.LabsActionInboxModel = store.LabsActionInboxModel.set("LabActionsInboxDisplaySettingsOption", open);

        if (open === true) {
            // IS THIS PROPER BEHAVIOR?
            // store.LabsActionInboxModel = store.LabsActionInboxModel.set("CliniciansPrescriptorSearchText","");
            // store.LabsActionInboxModel = store.LabsActionInboxModel.set("CliniciansActionBySearchText", "");
        }
        else {
            window.setTimeout(() => LocalEvent.Dispatch(LocalOperations.Labs.OnRefreshClick), 20);
        }
    }

    private OnContainerClick(request: LocalEvent, store: LabsStore): void {
        if (store.LabsActionInboxModel.get("LabActionsInboxDisplaySettingsOption") === true) {
            store.LabsActionInboxModel = store.LabsActionInboxModel.set("LabActionsInboxDisplaySettingsOption", false);
            labsStore.LabActions.RefreshActions(store.LabsActionInboxModel.get("CurrentInbox"), store.LabsActionInboxModel.get("GlobalActionSearchText"), store.LabsActionInboxModel.get("SortColumn"), store.LabsActionInboxModel.get("SortColumnDirection"));
            store.EmitChangeToListeners();
        }
    }
}

export { LabActionsInboxSettingsContainerHandler };
